import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Phone, Calendar, Heart, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import CustomerNotesSection from "./CustomerNotesSection";
import CustomerMembershipsSection from "./CustomerMembershipsSection";
import { formatCurrency } from "@/utils/currencyUtils";

interface Customer {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  total_bookings: number;
  last_booking_date: string;
  first_booking_date: string;
  total_spent: number;
  currency: string;
}

interface CustomerProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  date_of_birth?: string;
  gender?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
}

interface CustomerProfileDialogProps {
  customer: Customer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId?: string;
}

const CustomerProfileDialog = ({ customer, open, onOpenChange, providerId }: CustomerProfileDialogProps) => {
  const { data: customerProfile, isLoading } = useQuery({
    queryKey: ['customer-profile', customer?.customer_email],
    queryFn: async () => {
      if (!customer?.customer_email) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', customer.customer_email)
        .single();

      if (error) {
        console.error('Error fetching customer profile:', error);
        return null;
      }

      return data as CustomerProfile;
    },
    enabled: !!customer?.customer_email && open,
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not provided';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const formatGender = (gender?: string) => {
    if (!gender) return 'Not provided';
    return gender.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Customer Profile
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Column - Customer Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Name:</span>
                      <span>{customer.customer_name}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Email:</span>
                      <span className="break-all">{customer.customer_email}</span>
                    </div>
                    
                    {customerProfile?.phone_number && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Phone:</span>
                        <span>{customerProfile.phone_number}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Date of Birth:</span>
                      <span>{formatDate(customerProfile?.date_of_birth)}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Gender:</span>
                      <span>{formatGender(customerProfile?.gender)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Emergency Contact Information */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  Emergency Contact
                </h3>
                
                {customerProfile?.emergency_contact_name || 
                 customerProfile?.emergency_contact_phone || 
                 customerProfile?.emergency_contact_relationship ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                    {customerProfile.emergency_contact_name && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-red-600" />
                        <span className="font-medium">Name:</span>
                        <span>{customerProfile.emergency_contact_name}</span>
                      </div>
                    )}
                    
                    {customerProfile.emergency_contact_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-red-600" />
                        <span className="font-medium">Phone:</span>
                        <span>{customerProfile.emergency_contact_phone}</span>
                      </div>
                    )}
                    
                    {customerProfile.emergency_contact_relationship && (
                      <div className="flex items-center gap-2">
                        <Heart className="h-4 w-4 text-red-600" />
                        <span className="font-medium">Relationship:</span>
                        <span>{formatGender(customerProfile.emergency_contact_relationship)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    <Heart className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No emergency contact information provided</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Booking Statistics */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Booking Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {customer.total_bookings}
                    </div>
                    <div className="text-sm text-blue-700">Total Bookings</div>
                  </div>
                  
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(customer.total_spent, customer.currency)}
                    </div>
                    <div className="text-sm text-green-700">Total Spent</div>
                  </div>
                  
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-sm font-bold text-purple-600">
                      {formatDate(customer.first_booking_date)}
                    </div>
                    <div className="text-sm text-purple-700">First Booking</div>
                  </div>
                  
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-sm font-bold text-orange-600">
                      {formatDate(customer.last_booking_date)}
                    </div>
                    <div className="text-sm text-orange-700">Last Booking</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Memberships and Notes */}
            <div className="lg:col-span-2 space-y-6">
              {/* Memberships Section */}
              <div>
                <CustomerMembershipsSection customerEmail={customer.customer_email} />
              </div>

              <Separator />

              {/* Customer Notes */}
              <div>
                {providerId && (
                  <CustomerNotesSection 
                    providerId={providerId} 
                    customerEmail={customer.customer_email} 
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerProfileDialog;
